import { type CSSProperties, type PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { getSubscriptionProductConfig } from '~/configuration/repub';
import { formatPrice } from '~/configuration/utils/formatPrice';
import { useTrackCTAImpression } from '~/hooks/use-track-cta-impression.ts';
import { useTrackGZMetaPixelSubscriptionClick } from '~/pages/gz/hooks/useTrackGZMetaPixelSubscriptionClick';
import { type Repub } from '~/vendor/cx-modules/constants';
import { SUBSCRIPTION_TERM } from '~/vendor/cx-sdk/subs/schema';
import { type SubsProduct } from '~/vendor/cx-web/domain-types';
import { isSupportedRepub } from '~/vendor/cx-web/repub-config';
import { type RepubIs } from '~/vendor/cx-web/utils';
import { getCurrencySymbol } from '~/vendor/cx-web/utils/getCurrencySymbol';
import { Button } from '~/vendor/cxy/components/Button';

import styles from './styles/PriceCard.module.css';

export type PriceCardProps = {
  onClick?: () => void;
  product: SubsProduct;
  repub: Repub;
  repubIs: RepubIs;
  subscriptionLinkEnv: 'PROD' | 'NON_PROD';
  userHasCancelledSubscription: boolean;
} & PropsWithChildren;

export function PriceCard({
  onClick,
  product,
  repub,
  repubIs,
  subscriptionLinkEnv,
  userHasCancelledSubscription,
  ...props
}: PriceCardProps) {
  const ref = useTrackCTAImpression({
    price: product.price,
    product: product.term,
    title: 'oddschecker_plus',
    wallType: 'Landing Page',
  });

  const isAnnual = product.term === SUBSCRIPTION_TERM.ANNUAL;
  const monthlyPrice = Math.floor(product.amount / (isAnnual ? 12 : 1)) / 100;
  const monthlyPriceParts = formatPrice(monthlyPrice);
  const { i18n, t } = useTranslation(['label']);

  const productConfig = isSupportedRepub(repub) ? getSubscriptionProductConfig(repub, product.productId) : undefined;
  const display = productConfig?.display;

  const externalUrl = productConfig?.externalUrl?.[subscriptionLinkEnv];
  const shouldUseExternalLink = Boolean(externalUrl);

  const displayKey = (field: string) => `${display!.labelKeyPrefix}_${field}`;
  const optionalLabel = (field: string) => (i18n.exists(displayKey(field)) && t(displayKey(field))) || undefined;

  const badgeText = display ? optionalLabel('BADGE') : t('CX_BEST_VALUE');
  const isFeaturedPlan = display ? Boolean(badgeText) : isAnnual;
  const termText = display ? optionalLabel('TERM') : t(isAnnual ? 'CX_ANNUAL' : 'CX_MONTHLY');
  const offerDisclaimerText = display ? optionalLabel('TAGLINE') : t('CX_OFFER_DISCLAIMER');
  const paidTermText = display ? optionalLabel('BILLING_NOTE') : t(isAnnual ? 'CX_PAID_ANNUALLY' : 'CX_PAID_MONTHLY');
  const buttonText = t(
    display ? displayKey('CTA') : userHasCancelledSubscription ? 'CX_SUBSCRIBE_NOW' : 'CX_PLUS_START_7_DAY_TRIAL',
  );

  const trackGZMetaPixelClick = useTrackGZMetaPixelSubscriptionClick({
    productId: product.productId,
    externalUrl: repubIs.GZ ? externalUrl : undefined,
    ctaName: buttonText.toLowerCase().replace(/\s+/g, '_'),
  });

  return (
    <div
      className={clsx(styles.priceSelection, isFeaturedPlan && styles.bestValueBorder, display && styles.configured)}
      key={product.id}
      ref={ref}
      style={display && ({ '--price-card-desktop-order': display.desktopOrder } as CSSProperties)}
      {...props}
    >
      {isFeaturedPlan && (
        <p className={styles.bestValueTag} data-testid="badge">
          {badgeText}
        </p>
      )}
      <div className={styles.header}>
        {termText && (
          <p className={styles.term} data-testid="term">
            {termText}
          </p>
        )}
        {offerDisclaimerText && (
          <p className={styles.offerDisclaimer} data-testid="tagline">
            {offerDisclaimerText}
          </p>
        )}
      </div>
      <p className={styles.price} data-testid="price">
        {display ? (
          <Trans components={{ amount: <span className={styles.monthlyCost} /> }} i18nKey={displayKey('PRICE')} t={t} />
        ) : (
          <>
            <span className={styles.monthlyCost}>
              {getCurrencySymbol(product.currency)}
              {monthlyPriceParts.whole}
            </span>
            {monthlyPriceParts.decimal !== '00' && `.${monthlyPriceParts.decimal}`} /{t('CX_MONTH')}
          </>
        )}
      </p>
      <p className={styles.paid} data-testid="paid-term">
        {paidTermText}
      </p>
      {shouldUseExternalLink ? (
        <Button asChild className={styles.subscribeButton} size="large" variant="secondary">
          <a
            href={externalUrl}
            onClick={() => {
              trackGZMetaPixelClick();
              onClick?.();
            }}
            rel="noopener noreferrer"
            target="_blank"
          >
            {buttonText}
          </a>
        </Button>
      ) : (
        <Button className={styles.subscribeButton} onClick={onClick} size="large" variant="secondary">
          {buttonText}
        </Button>
      )}
    </div>
  );
}
